<template>
  <v-dialog v-model="showModal" width="480">
    <modal-card title="Profile Settings" padding @close="showModal = false">
      <v-form ref="form" @submit.prevent>
        <div class="user-picture mb-1">
          <spinner v-if="imageProcessing" />
          <img v-else-if="settings.avatar" :src="settings.avatar" alt="" />
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"
            />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </div>
        <div class="text-center mb-4 mt-2">
          <v-btn
            color="appMain"
            @click="pickFile"
            v-if="isHost"
            small
            class="ma-1"
          >
            Upload Photo
          </v-btn>
          <v-btn
            color="appMain"
            @click="showSelfieModal = true"
            small
            class="ma-1"
          >
            Take Selfie
          </v-btn>
        </div>
      </v-form>

      <template v-slot:actions>
        <v-btn color="appMain" @click="saveChanges" :disabled="!hasChanges">
          Save Changes
        </v-btn>
      </template>

      <take-selfie-modal v-model="showSelfieModal" @save="uploadImage" />
    </modal-card>
  </v-dialog>
</template>

<script>
import FilestackLib from '@/lib/filestackLib';
import { eventBus } from '@/lib/events';
import { mapGetters, mapState, mapMutations } from 'vuex';
import analyticSender from '../helpers/analyticSender';
import isEmpty from 'lodash/isEmpty';

import TakeSelfieModal from '@/components/rooms/components/TakeSelfieModal';

const PROFILE_KEY = 'rooms-profile-settings';

export default {
  name: 'RoomProfileSettings',
  components: {
    TakeSelfieModal,
  },
  data() {
    return {
      imageProcessing: false,
      showModal: false,
      showSelfieModal: false,
      settings: {
        avatar: '',
      },
    };
  },
  computed: {
    ...mapState('rooms', ['roomInfo']),
    ...mapGetters('rooms', ['isHost']),
    currentUser() {
      return this.roomInfo;
    },
    identity() {
      return this.currentUser?.identity;
    },
    hasChanges() {
      return Object.keys(this.settings).some((key) => {
        return this.settings[key] !== this.currentUser[key];
      });
    },
  },
  watch: {
    showModal(val) {
      if (val) {
        this.setFormData();
      } else {
        this.clearData();
      }
    },
    roomInfo: {
      immediate: true,
      handler(newVal, oldVal) {
        if (isEmpty(oldVal) && !isEmpty(newVal)) {
          this.extractSavedSettings();
        }
      },
    },
  },
  methods: {
    ...mapMutations('rooms', ['setRoomInfo']),
    fileName(text) {
      let id = this.identity.replace(/\_/g, '-');
      let part = text ? text : id;
      return `rooms-avatar-${part}`;
    },

    pickFile() {
      let pickerOptions = {
        accept: ['image/*'],
        maxFiles: 1,
        transformations: {
          crop: {
            aspectRatio: 4 / 3,
            force: true,
          },
        },
        onFileSelected: (file) => {
          return { ...file, name: this.fileName() };
        },
        onFileUploadFailed: (file) => {
          console.warn('file failed to upload', file);
        },
        onFileUploadFinished: ({ handle }) => {
          this.setAvatar(handle);
        },
      };

      let fs = new FilestackLib();
      fs.pick(pickerOptions);
    },

    uploadImage(dataUrl) {
      this.imageProcessing = true;
      let fs = new FilestackLib();
      let name = this.fileName();

      fs.upload({
        file: dataUrl,
        name,
      }).then(({ handle }) => {
        this.imageProcessing = false;
        this.setAvatar(handle);
      });
    },

    setAvatar(handle) {
      this.settings.avatar = `https://cdn.filestackcontent.com/crop_faces=mode:thumb/${handle}`;
    },

    saveChanges() {
      if (this.selectedUser) {
        this.updateParticipant();
      } else {
        this.updateProfile(this.settings);
      }

      this.showModal = false;
    },

    updateParticipant() {
      eventBus.trigger('participantInfoUpdated', {
        info: {
          identity: this.selectedUser.identity,
          ...this.settings,
        },
        options: { to: 'host' },
      });
    },

    updateProfile(settings) {
      this.setRoomInfo({ ...this.roomInfo, ...settings });
      localStorage.setItem(PROFILE_KEY, JSON.stringify(settings));

      if (this.isHost) return;
      analyticSender('update_profile_settings', {
        set_image: settings.avatar,
      });
    },

    setFormData() {
      let data = {};
      if (this.selectedUser) {
        data = this.selectedUser;
      } else {
        data = JSON.parse(localStorage.getItem(PROFILE_KEY)) || this.roomInfo;
      }

      for (let key in this.settings) {
        this.settings[key] = data[key];
      }
    },

    checkProfileSettings() {
      if (!this.roomInfo?.avatar) {
        this.showModal = true;
      }
    },

    async extractSavedSettings() {
      await this.$nextTick();
      const settings = JSON.parse(localStorage.getItem(PROFILE_KEY)) || {};
      if (!isEmpty(settings)) {
        this.setRoomInfo({ ...this.roomInfo, ...settings });
      }
    },

    clearData() {
      this.$refs.form.reset();
    },
  },
  mounted() {
    eventBus.once('checkRoomProfileSettings', () => {
      this.checkProfileSettings();
    });
    eventBus.on('showRoomProfileModal', () => {
      this.showModal = true;
    });
  },
};
</script>
